import { useState } from 'react';

import { formatMinorAmount } from '@repo/common';
import { type BillingPlanSummaryQuery, DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSSeparator } from '@repo/sds/SDSSeparator';
import { SDSText } from '@repo/sds/SDSText';

import { formatBillingDate } from '../billing-format';
import { describePlanKind } from '../billing-plan-format';
import { PackageActions } from '../payment-summary';
import { RefundDetailsDialog } from './refund-details-dialog';

type BillingPlanSummary = NonNullable<BillingPlanSummaryQuery['myBillingPlanSummary']>;

interface RefundedPackageStateProps {
  plan: BillingPlanSummary;
  isReceiptLoading: boolean;
  onViewReceipt(): void;
}

function RefundFigure({
  heading,
  amount,
  lines,
  dataTestId,
}: {
  heading: string;
  amount: string;
  lines: (string | undefined)[];
  dataTestId: string;
}) {
  return (
    <SDSFlexbox
      className="RefundFigure"
      direction="column"
      gap="text-small"
      alignItems="flex-start"
      dataTestId={dataTestId}
    >
      <SDSText type="body-large" text={heading} color="text-secondary" />
      <SDSText type="h4" text={amount} />
      {lines.filter(Boolean).map(line => (
        <SDSText key={line} type="body-large" text={line ?? ''} color="text-secondary" />
      ))}
    </SDSFlexbox>
  );
}

/**
 * What the Your Package card becomes once the purchase has been refunded.
 *
 * The plan is gone but the history is not, so this states both figures side by
 * side rather than emptying the card: someone checking that their money came
 * back wants to see the two numbers agree.
 */
export function RefundedPackageState({ plan, isReceiptLoading, onViewReceipt }: RefundedPackageStateProps) {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const planLabel = describePlanKind(plan.kind);
  const refund = plan.refund;

  return (
    <SDSFlexbox direction="column" gap="no-design-24" alignItems="flex-start" fullWidth>
      {/* H3 in the refunded frame — larger than the plan kinds' H4, because here
          it is the whole message rather than a label over the figures. */}
      <SDSText type="h3" text="No active package" dataTestId={DataTestIds.BILLING.PACKAGE_TITLE} />
      {/* The frame sets this sentence at the H5 size; it is still a sentence, so
          it keeps a paragraph's element. */}
      <SDSText
        type="h5"
        as="p"
        text={`Your ${plan.productTitle} purchase has been refunded and your ${plan.productTitle} access has ended.`}
      />

      <SDSSeparator />

      {/* Two equal columns with a rule between them, 24px apart, as drawn. SDS
          has no token for the frame's 24px content gap, hence no-design-24. */}
      <SDSFlexbox direction="row" gap="no-design-24" alignItems="stretch" fullWidth>
        <RefundFigure
          heading="Amount paid"
          amount={formatMinorAmount(plan.totalPaid.amountMinor, plan.totalPaid.currency)}
          lines={[plan.paidAt ? `Paid on ${formatBillingDate(plan.paidAt)}` : undefined, planLabel]}
          dataTestId={DataTestIds.BILLING.REFUND.REFUNDED_AMOUNT_PAID}
        />
        {refund ? <SDSSeparator className="FactRule" orientation="vertical" /> : null}
        {refund ? (
          <RefundFigure
            heading="Amount refunded"
            amount={formatMinorAmount(refund.amount.amountMinor, refund.amount.currency)}
            lines={[
              refund.refundedAt ? `Refunded on ${formatBillingDate(refund.refundedAt)}` : undefined,
              'Returned to the original payment method',
            ]}
            dataTestId={DataTestIds.BILLING.REFUND.REFUNDED_AMOUNT_REFUNDED}
          />
        ) : null}
      </SDSFlexbox>

      <PackageActions>
        {/* A refund does not take the original receipt away: this is still the
            receipt for the payment that was made, which is what someone
            reconciling a refund needs to see next to it. */}
        <SDSButton
          className="PackageActions__receipt"
          text="View original receipt"
          mode="outline"
          size="medium"
          isSubmitting={isReceiptLoading}
          onClick={onViewReceipt}
          dataTestId={DataTestIds.BILLING.REFUND.VIEW_ORIGINAL_RECEIPT}
        />
        {refund ? (
          <SDSButton
            text="View refund details"
            mode="primary"
            size="medium"
            onClick={() => {
              setIsDetailsOpen(true);
            }}
            dataTestId={DataTestIds.BILLING.REFUND.VIEW_REFUND_DETAILS}
          />
        ) : null}
      </PackageActions>

      {refund ? (
        <RefundDetailsDialog
          isOpen={isDetailsOpen}
          productTitle={plan.productTitle}
          planLabel={planLabel}
          refund={refund}
          onClose={() => {
            setIsDetailsOpen(false);
          }}
        />
      ) : null}
    </SDSFlexbox>
  );
}
